import { useParams } from 'react-router'
import { Loader2, Phone, AlertCircle } from 'lucide-react'
import { useDemoAgent } from './useDemoApi'
import { DemoVoiceCall } from './DemoVoiceCall'
import { VapiCallButton } from '@/features/vapiCallButton'

export function DemoPage() {
  const { agentAltKey } = useParams<{ agentAltKey: string }>()
  const { data: agent, isLoading, error } = useDemoAgent(agentAltKey ?? '')

  if (isLoading) {
    return (
      <div className="h-screen flex items-center justify-center bg-gray-50">
        <Loader2 className="w-8 h-8 animate-spin text-gray-400" />
      </div>
    )
  }

  if (error || !agent) {
    return (
      <div className="h-screen flex items-center justify-center bg-gray-50">
        <div className="text-center max-w-sm px-6">
          <div className="w-14 h-14 rounded-full bg-red-50 flex items-center justify-center mx-auto mb-4">
            <AlertCircle className="w-6 h-6 text-red-400" />
          </div>
          <h1 className="text-heading font-semibold text-gray-800 mb-1">Agent not found</h1>
          <p className="text-body text-gray-500">
            This demo link is invalid or has expired. Please contact the person who shared it.
          </p>
        </div>
      </div>
    )
  }

  return (
    <div className="h-screen flex flex-col bg-gray-50">
      <header className="shrink-0 border-b border-gray-200 bg-white px-6 py-3">
        <div className="flex items-center justify-between max-w-7xl mx-auto">
          <div className="flex items-center gap-3">
            <div className="w-8 h-8 rounded-lg bg-emerald-500 flex items-center justify-center">
              <span className="text-white font-bold text-body">I</span>
            </div>
            <div>
              <h1 className="text-body font-semibold text-gray-800">{agent.agentName || 'AI Agent'}</h1>
              <p className="text-detail text-gray-400">{agent.campaignName}</p>
            </div>
          </div>
          {agent.voiceName && (
            <span className="text-detail text-gray-400 bg-gray-50 px-2 py-1 rounded">
              Voice: {agent.voiceName}
            </span>
          )}
        </div>
      </header>

      <div className="flex-1 flex flex-col min-h-0 bg-white">
        <div className="shrink-0 px-4 py-2.5 border-b border-gray-100 bg-gray-50/50">
          <div className="flex items-center gap-2">
            <Phone className="w-3.5 h-3.5 text-emerald-500" />
            <span className="text-detail font-medium text-gray-600">Voice</span>
          </div>
        </div>
        <div className="flex-1 min-h-0">
          {agent.vapiAgentId ? (
            <VapiCallButton
              vapiAgentId={agent.vapiAgentId}
              agentId={agentAltKey!}
              variant="full"
            />
          ) : (
            <DemoVoiceCall agentAltKey={agentAltKey!} />
          )}
        </div>
      </div>
    </div>
  )
}
